/**
 * Normalizes the page list off a validation result, preferring the
 * canonical `pageNumbers` array and falling back to the legacy single
 * `pageNumber` for results written before the schema change.
 */
export const getPageNumbers = (result: {
  pageNumber?: number | null;
  pageNumbers?: number[] | null;
}): number[] => {
  if (result.pageNumbers?.length) return result.pageNumbers;
  if (result.pageNumber != null) return [result.pageNumber];
  return [];
};

/**
 * Renders a list of page numbers as a human-readable label.
 *
 * - 0 pages: empty string
 * - 1 page: "Page 5"
 * - 2–5 pages: "Pages 3, 5, 7"
 * - 6+ pages: "Pages 3–29" (uses an en dash)
 */
export const formatPageList = (pageNumbers: number[]): string => {
  if (pageNumbers.length === 0) return '';
  if (pageNumbers.length === 1) return `Page ${pageNumbers[0]}`;
  if (pageNumbers.length <= 5) return `Pages ${pageNumbers.join(', ')}`;
  const first = pageNumbers[0];
  const last = pageNumbers[pageNumbers.length - 1];
  return `Pages ${first}–${last}`;
};

/**
 * One contract document's footprint inside the combined ingestion-document PDF.
 *
 * `startPage`/`endPage` are the absolute min/max and are NOT a contiguity
 * guarantee — a contract document's pages can interleave with a sibling's. Use
 * `pages` (absolute page numbers in document reading order) whenever you need to
 * reason about a specific page's position within the document; it is the
 * authoritative inverse of the canonical→absolute mapping.
 *
 * `pages` is optional because the map is derived from an ingestion document's
 * classification and older cached/serialized payloads predate the field.
 */
export interface ContractDocumentPageRange {
  startPage: number;
  endPage: number;
  /** Absolute pages in document reading order; index 0 is the document's page 1. */
  pages?: number[];
}

/**
 * Narrows a loosely-typed `contractDocumentPages` entry (the field crosses
 * process boundaries as a JSON scalar) into a {@link ContractDocumentPageRange}.
 * Returns `undefined` when the entry is missing or malformed.
 */
export const getContractDocumentPageRange = (
  contractDocumentPages: Record<string, unknown> | null | undefined,
  contractDocumentUuid: string | null | undefined
): ContractDocumentPageRange | undefined => {
  if (!contractDocumentPages || !contractDocumentUuid) return undefined;
  const entry = contractDocumentPages[contractDocumentUuid] as
    | { startPage?: unknown; endPage?: unknown; pages?: unknown }
    | null
    | undefined;
  if (!entry) return undefined;
  if (
    typeof entry.startPage !== 'number' ||
    typeof entry.endPage !== 'number'
  ) {
    return undefined;
  }

  // All-or-nothing: `pages` is positional — a page's index IS its document page
  // number — so dropping a malformed element would shift every later page down
  // one and silently mislabel them. A partially-valid array is treated as
  // unusable so callers fall back to `startPage` arithmetic instead.
  const pages =
    Array.isArray(entry.pages) &&
    entry.pages.length > 0 &&
    entry.pages.every((page) => typeof page === 'number')
      ? (entry.pages as number[])
      : undefined;

  return {
    startPage: entry.startPage,
    endPage: entry.endPage,
    pages,
  };
};

/**
 * Orders the contract documents inside one ingestion document by where each
 * first appears in the combined PDF, as `contractDocumentUuid` → 0-based
 * ordinal.
 *
 * Needed because a contract document's own page numbers are only meaningful
 * alongside the document they belong to: once messages say "Page 3" of the
 * addendum (BLO-13411), a list ordered by absolute PDF page reads as a jumble
 * of restarting page numbers. Grouping by this ordinal first, then by document
 * page, makes the list walk the bundle document by document.
 *
 * Keyed on `startPage` (the lowest absolute page) rather than `pages[0]` (the
 * absolute page of the document's own page 1) so the ordinal answers "where
 * does this document first show up in the bundle" — the physical reading order
 * a reviewer scrolling the PDF sees, which stays sensible even when two
 * documents' pages interleave.
 *
 * Ties break on uuid so the order is stable across requests. Contract documents
 * missing from the map are absent from the result; callers should treat a
 * missing ordinal as "sorts last".
 */
export const getContractDocumentPageOrder = (
  contractDocumentPages?: Record<string, unknown> | null
): Map<string, number> => {
  const order = new Map<string, number>();
  if (!contractDocumentPages) return order;

  const ranked: { uuid: string; startPage: number }[] = [];
  for (const uuid of Object.keys(contractDocumentPages)) {
    const range = getContractDocumentPageRange(contractDocumentPages, uuid);
    if (!range) continue;
    ranked.push({ uuid, startPage: range.startPage });
  }

  ranked.sort((a, b) =>
    a.startPage !== b.startPage
      ? a.startPage - b.startPage
      : a.uuid.localeCompare(b.uuid)
  );

  ranked.forEach(({ uuid }, index) => order.set(uuid, index));

  return order;
};

/**
 * Converts absolute PDF page numbers into pages relative to a single contract
 * document, so messages read "Page 3" (the addendum's own page 3) instead of
 * "Page 25" (its position in the combined PDF) — see BLO-13411.
 *
 * Prefers `pages`, where a page's 1-based position in reading order IS its
 * document page, which stays correct when a document's pages interleave with a
 * sibling's. Falls back to `startPage` arithmetic when `pages` is absent, which
 * is right for the common contiguous case.
 *
 * Absolute pages outside the document are dropped rather than mislabelled;
 * if that empties the list, returns the input unchanged so callers still render
 * *something* rather than silently losing page attribution.
 */
export const toDocumentRelativePages = (
  absolutePageNumbers: number[],
  range: ContractDocumentPageRange | undefined
): number[] => {
  if (!range || absolutePageNumbers.length === 0) return absolutePageNumbers;

  const converted: number[] = [];
  for (const absolutePage of absolutePageNumbers) {
    if (range.pages) {
      const index = range.pages.indexOf(absolutePage);
      if (index >= 0) converted.push(index + 1);
      continue;
    }
    if (absolutePage >= range.startPage && absolutePage <= range.endPage) {
      converted.push(absolutePage - range.startPage + 1);
    }
  }

  if (converted.length === 0) return absolutePageNumbers;

  return Array.from(new Set(converted)).sort((a, b) => a - b);
};

/**
 * Page count of a SINGLE contract document (not the whole combined PDF), used
 * for {@link formatValidationMessage}'s single-page prefix suppression. Returns
 * `0` when unknown, which keeps the prefix.
 */
export const getContractDocumentPageCount = (
  range: ContractDocumentPageRange | undefined
): number => {
  if (!range) return 0;
  if (range.pages) return range.pages.length;

  return Math.max(0, range.endPage - range.startPage + 1);
};

/**
 * Total page count of an ingestion document, derived from its
 * `contractDocumentPages` map (`{ [contractDocumentUuid]: { startPage, endPage } }`,
 * where pages are absolute within the ingestion document PDF).
 *
 * Returns the highest `endPage` across all contract documents, or `0` when the
 * map is empty/unavailable (e.g. the document hasn't been classified yet, so
 * page ranges haven't been computed). The scalar is a loosely-typed JSON
 * object, so each value is defensively narrowed before reading `endPage`.
 */
export const getDocumentPageCount = (
  contractDocumentPages?: Record<string, unknown> | null
): number => {
  if (!contractDocumentPages) return 0;

  let maxPage = 0;
  for (const value of Object.values(contractDocumentPages)) {
    const endPage = (value as { endPage?: unknown } | null | undefined)
      ?.endPage;
    if (typeof endPage === 'number' && endPage > maxPage) {
      maxPage = endPage;
    }
  }

  return maxPage;
};

/**
 * Composes a validation result's message with its failing-page list.
 * Returns the raw message when no pages are attributed.
 *
 * Single-page documents omit the page prefix: every issue is on the only
 * page, so "Page 1 - …" is noise. Pass `totalDocumentPages` (see
 * {@link getDocumentPageCount}) to enable this; when it is exactly `1` the
 * prefix is suppressed. Omit it (or pass an unknown count of `0`) to keep the
 * prefix on every page-attributed result.
 */
export const formatValidationMessage = (
  message: string,
  pageNumbers: number[],
  totalDocumentPages?: number
): string => {
  if (pageNumbers.length === 0) return message;
  if (totalDocumentPages === 1) return message;
  return `${formatPageList(pageNumbers)} - ${message}`;
};
